import { useEffect, useMemo, useState } from 'react';
import {
  getTrackingStreamUrl,
  parseTrackingStreamPayload,
  type LocationData,
} from '@/services/tracking';

/**
 * Inscreve o paciente no SSE de localização para consultas em deslocamento.
 * Quando o médico envia GPS, o mapa em Minhas Consultas atualiza.
 */
export function useDoctorTracking(inTransitAppointmentIds: string[]) {
  const [trackingData, setTrackingData] = useState<
    Record<string, LocationData>
  >({});

  const appointmentKey = useMemo(
    () => [...new Set(inTransitAppointmentIds.filter(Boolean))].sort().join(','),
    [inTransitAppointmentIds],
  );

  useEffect(() => {
    const ids = appointmentKey ? appointmentKey.split(',') : [];
    if (ids.length === 0) {
      setTrackingData({});
      return;
    }

    const sources: EventSource[] = [];

    for (const appointmentId of ids) {
      const url = getTrackingStreamUrl(appointmentId);
      const source = new EventSource(url);

      source.onmessage = (event) => {
        const payload = parseTrackingStreamPayload(event.data);
        if (!payload) return;

        if ('status' in payload && payload.status === 'arrived') {
          setTrackingData((prev) => {
            if (!(appointmentId in prev)) return prev;
            const next = { ...prev };
            delete next[appointmentId];
            return next;
          });
          return;
        }

        if ('latitude' in payload) {
          setTrackingData((prev) => ({
            ...prev,
            [appointmentId]: {
              latitude: payload.latitude,
              longitude: payload.longitude,
              timestamp: payload.timestamp,
            },
          }));
        }
      };

      source.onerror = () => {
        if (import.meta.env.DEV) {
          console.warn(
            '[Tracking] SSE desconectado, o browser tentará reconectar:',
            appointmentId,
          );
        }
      };

      sources.push(source);
    }

    return () => {
      for (const source of sources) {
        source.close();
      }
    };
  }, [appointmentKey]);

  return trackingData;
}
